import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import type { PersonalAccessToken } from './usePersonalAccessTokens'

const GeneratePersonalAccessTokenMutation = graphql(/* GraphQL */ `
  mutation generatePersonalAccessToken(
    $input: GeneratePersonalAccessTokenInput!
  ) {
    generatePersonalAccessToken(input: $input) {
      ... on GeneratePersonalAccessTokenSuccess {
        personalAccessToken {
          accessToken
          clientId
          name
          generatedAt
          expiresAt
          scopes
        }
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

type GenerateTokenResult =
  | { success: true; token: PersonalAccessToken }
  | { success: false; error: string }

type GenerateTokenInput = {
  name?: string
  expiresIn?: number
  scopes?: string[]
}

type UseGeneratePersonalAccessTokenResult = {
  generateToken: (input?: GenerateTokenInput) => Promise<GenerateTokenResult>
  isLoading: boolean
}

export const useGeneratePersonalAccessToken =
  (): UseGeneratePersonalAccessTokenResult => {
    const [mutate, { loading }] = useMutation(
      GeneratePersonalAccessTokenMutation,
    )

    const generateToken = async (
      input: GenerateTokenInput = {},
    ): Promise<GenerateTokenResult> => {
      try {
        const result = await mutate({
          variables: { input },
        })

        if (result.error) {
          return { success: false, error: result.error.message }
        }

        const response = result.data?.generatePersonalAccessToken

        if (response && 'personalAccessToken' in response) {
          return { success: true, token: response.personalAccessToken }
        }

        if (response && 'message' in response) {
          return { success: false, error: response.message }
        }

        return {
          success: false,
          error: 'Failed to generate token. Please try again.',
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred. Please try again.'

        return { success: false, error: message }
      }
    }

    return {
      generateToken,
      isLoading: loading,
    }
  }
